import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Copy, CheckCircle2 } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null, copied: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[MOBILBO] Uncaught error:', error, errorInfo);
    this.setState({ error, errorInfo });

    // Auto-reload on Chunk Load / Dynamic Import failures
    const errorStr = [
      error?.message,
      error?.name,
      error?.stack,
      error?.toString?.(),
      String(error)
    ].filter(Boolean).join(' ').toLowerCase();

    if (
      errorStr.includes('failed to fetch dynamically') ||
      errorStr.includes('chunkloaderror') ||
      errorStr.includes('dynamically imported module') ||
      errorStr.includes('loading chunk') ||
      errorStr.includes('error loading')
    ) {
      let shouldReload = false;
      const reloadKey = 'mobilbo_chunk_reload_attempted';
      const now = Date.now();

      try {
        const lastAttempt = sessionStorage.getItem(reloadKey);
        // If we haven't attempted to reload in this session within the last 10 seconds, reload
        if (!lastAttempt || now - parseInt(lastAttempt, 10) > 10000) {
          sessionStorage.setItem(reloadKey, now.toString());
          shouldReload = true;
        }
      } catch (e) {
        console.error('[MOBILBO] Storage error during ErrorBoundary reload handler, falling back to window.name:', e);
        try {
          const nameParts = window.name ? window.name.split('__') : [];
          const lastReload = nameParts[0] === reloadKey ? parseInt(nameParts[1], 10) : 0;
          if (now - lastReload > 10000) {
            window.name = `${reloadKey}__${now}`;
            shouldReload = true;
          }
        } catch {
          shouldReload = true;
        }
      }

      if (shouldReload) {
        console.warn('[MOBILBO] Chunk load error detected, triggering auto-reload...');
        window.location.reload();
      }
    }
  }

  private handleReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  private copyError = () => {
    const isProd = import.meta.env.PROD;
    const errorText = isProd
      ? `Error: ${this.state.error?.message || 'Erreur inattendue'}\nSession Code: ${Date.now()}`
      : `
Error: ${this.state.error?.message}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
    `.trim();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(errorText).then(() => {
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2000);
      }).catch(err => {
        console.error("Failed to copy:", err);
      });
    } else {
      console.warn("Clipboard API not available or blocked in iframe");
      // Optionally show a fallback message in state
      this.setState({ copied: false });
    }
  };

  public render() {
    if (this.state.hasError) {
      const isProd = import.meta.env.PROD;
      return (
        <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-4 md:p-8 font-main selection:bg-primary/30">
          <div className="max-w-3xl w-full bg-[#0F172A] border border-slate-800/50 rounded-[2rem] md:rounded-[3rem] p-8 md:p-16 shadow-2xl relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-600/10 blur-[100px] rounded-full"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-red-600/5 blur-[100px] rounded-full"></div>

            <div className="relative z-10">
              <div className="w-20 h-20 bg-red-500/10 rounded-3xl flex items-center justify-center mb-10 border border-red-500/20">
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
              
              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight leading-tight">
                Oups ! Quelque chose <br className="hidden md:block" /> s'est mal passé.
              </h1>
              
              <p className="text-slate-400 font-medium mb-10 text-lg max-w-xl">
                L'application a rencontré une erreur inattendue. Ne vous inquiétez pas, vos données sont en sécurité.
              </p>
              
              <div className="bg-[#020617] rounded-3xl p-6 md:p-8 border border-slate-800/50 mb-10 group transition-all hover:border-slate-700/50">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Détails techniques</span>
                  <button 
                    onClick={this.copyError}
                    className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                  >
                    {this.state.copied ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        <span>Copié !</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copier l'erreur</span>
                      </>
                    )}
                  </button>
                </div>
                
                <p className="text-red-400 font-mono text-sm mb-6 break-words leading-relaxed">
                  {this.state.error ? this.state.error.toString() : 'Erreur inconnue'}
                </p>
                
                {!isProd && (
                  <details className="text-slate-600 font-mono text-[10px] leading-relaxed">
                    <summary className="cursor-pointer hover:text-slate-400 transition-colors mb-3 font-bold uppercase tracking-widest flex items-center gap-2">
                      <span>Voir la pile d'exécution</span>
                      <div className="h-px flex-grow bg-slate-800/50"></div>
                    </summary>
                    <pre className="whitespace-pre-wrap opacity-40 max-h-48 overflow-y-auto custom-scrollbar pr-4">
                      {this.state.errorInfo ? this.state.errorInfo.componentStack : 'Aucune information de composant disponible.'}
                      {this.state.error?.stack && `\n\nStack Trace:\n${this.state.error.stack}`}
                    </pre>
                  </details>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button 
                  onClick={() => window.location.reload()}
                  className="flex items-center justify-center gap-2 bg-white text-slate-950 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all shadow-xl shadow-white/5 group text-xs"
                >
                  <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
                  Recharger
                </button>
                
                <button 
                  onClick={this.handleReset}
                  className="flex items-center justify-center gap-2 bg-slate-800/50 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-red-500 transition-all border border-slate-700/50 group text-xs"
                >
                  <Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  Réinitialiser
                </button>
              </div>
              
              <p className="mt-10 text-center text-slate-600 text-[10px] font-bold uppercase tracking-[0.3em]">
                {(typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.startsWith('127.') ? window.location.hostname.replace('www.', '') : 'Mobilbo')} Engine v2.0
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
